import { useEffect, useRef } from "react";

import { formatTimestamp } from "../lib/formatTime";

/**
 * ADR-0159 §6 — what a tab shows when its save was refused because the object
 * moved underneath it.
 *
 * A banner, not a modal: this is a state the tab is in, not a decision waiting
 * on an answer. The canvas stays interactive so nothing in progress is trapped,
 * and autosave pauses so the same refused write does not repeat every 350 ms.
 *
 * It says three things, in this order, because that is the order the reader
 * needs them: something changed, who changed it and when, and what reloading
 * will cost. The cost sentence is deliberately concrete — with a debounced
 * autosave the unsaved delta is almost always the last gesture, and saying so
 * is what makes "Reload latest" an easy click instead of a frightening one.
 */
export function WriteConflictBanner(props: {
  /** "pipeline" | "page" — the noun the reader is actually looking at. */
  noun: string;
  actorLabel: string;
  sourceDisplay: string;
  at: string;
  onReload: () => void;
  onCopy: () => void;
  /** Set while the reload is in flight, so the button cannot be double-fired. */
  busy?: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Takes focus on appearance: a save that silently stopped working is exactly
  // the kind of state a screen-reader user would otherwise never learn about.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div ref={ref} className="writeConflictBanner" role="alert" tabIndex={-1}>
      <div className="writeConflictBannerText">
        <strong>This {props.noun} changed while this tab was open.</strong>{" "}
        {props.actorLabel} made changes via {props.sourceDisplay} at {formatTimestamp(props.at)}. Reloading gets the
        current version; changes made in this tab since your last save will be lost (usually just your last gesture).
      </div>
      <div className="actions">
        <button className="btn btnPrimary" disabled={props.busy} onClick={props.onReload}>
          Reload latest
        </button>
        <button className="btn" disabled={props.busy} onClick={props.onCopy}>
          Copy my version as JSON
        </button>
      </div>
    </div>
  );
}
